import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, updateDoc, arrayUnion, arrayRemove, writeBatch, where, Timestamp, getDocs, doc, deleteDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { generateTimestampId } from '../utils/documentIdUtils';
import { logUserAction, logEntityDeletion } from '../utils/logger';
import { useAuth } from '../components/auth/AuthContext';
import Toast from '../components/common/Toast';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import ReportSubscriptionsPanel, { AVAILABLE_REPORTS } from '../components/common/ReportSubscriptionsPanel';
import { hasPermission, FEATURE_PERMISSIONS } from '../utils/permissions';
import PaginationControls from '../components/common/PaginationControls';

const functions = getFunctions();

const DATE_RANGE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' }
];

const NotificationsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Notifications management state
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useUrlState('activeTab', 'all'); // all, recent, important
  const fb = useFilterBar({ searchTerm: '' });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [currentPage, setCurrentPage] = useUrlState('currentPage', 1);
  const PAGE_SIZE = 25;
  const [reportSubscriptions, setReportSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [savingSubscription, setSavingSubscription] = useState(false);
  // Report-specific custom schedule state (keyed by report ID)
  const [reportCustomStates, setReportCustomStates] = useState({});
  // Track which subscription is being edited (null = not editing, subscription object = editing)
  const [editingSubscription, setEditingSubscription] = useState(null);
  // Global custom schedule for "Apply to All"
  const [globalCustomSchedule, setGlobalCustomSchedule] = useState({ cadence: 'daily', day: 'monday', time: '07:00' });
  const [showGlobalCustomSchedule, setShowGlobalCustomSchedule] = useState(false);
  // History and preview state
  const [sendHistory, setSendHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previewingReport, setPreviewingReport] = useState(null);
  const [userTimezone, setUserTimezone] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Rate limiting for "Send Immediately" - track last 4 sends
  const [sendTimestamps, setSendTimestamps] = useState({}); // Keyed by reportId for per-report rate limiting

  // Detect user's timezone on mount
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(tz);
      console.log('Detected user timezone:', tz);
    } catch (err) {
      console.error('Error detecting timezone:', err);
      setUserTimezone('America/New_York'); // Fallback to EST
    }
  }, []);

  // Subscribe to notifications in real-time
  useEffect(() => {
    if (!currentUser) return;
    
    const q = query(
      collection(db, collectionPath('notifications')),
      orderBy('createdAt', 'desc')
    );
    
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filter for recipients: empty (broadcast) or includes current user
      const visible = docs.filter(n => 
        !Array.isArray(n.recipients) || 
        n.recipients.length === 0 || 
        n.recipients.includes(currentUser.uid)
      );
      setNotifications(visible);
      setLoading(false);
    });
    
    return () => unsub();
  }, [currentUser]);

  // Fetch report subscriptions with real-time updates
  useEffect(() => {
    if (!currentUser?.uid) return;
    
    setLoadingSubscriptions(true);
    const q = query(
      collection(db, collectionPath('reportSubscriptions')),
      where('userId', '==', currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const subs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setReportSubscriptions(subs);
        setLoadingSubscriptions(false);
      },
      (err) => {
        console.warn('Failed to load report subscriptions:', err);
        setLoadingSubscriptions(false);
      }
    );
    
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // Helper function to format time from 24-hour to 12-hour format
  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Toggle report subscription
  // Create a new subscription variant (for reports with multiple subscriptions)
  const createSubscriptionVariant = async (reportId, customScheduleData) => {
    if (!currentUser?.uid || savingSubscription) return;
    
    setSavingSubscription(true);
    try {
      const newSub = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        reportId,
        frequency: 'custom',
        timezone: userTimezone || 'America/New_York',
        dateRange: customScheduleData.dateRange || null,
        customSchedule: customScheduleData,
        createdAt: serverTimestamp(),
        isActive: true
      };
      
      // Generate truly unique ID by including timestamp AND random suffix
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const dateRangeSuffix = customScheduleData.dateRange ? `_${customScheduleData.dateRange}d` : '';
      const docId = `reportsub_${currentUser.uid}_${reportId}${dateRangeSuffix}_${timestamp}_${randomSuffix}`;
      
      await setDoc(doc(db, collectionPath('reportSubscriptions'), docId), newSub);
      setReportSubscriptions(prev => [...prev, { id: docId, ...newSub }]);
      await logUserAction({
        action: 'Report Subscription Variant Created',
        details: `Created custom schedule variant for report "${reportId}" (cadence: ${customScheduleData?.cadence || 'custom'})`,
        entityType: 'ReportSubscription',
        entityId: docId
      });
      setToast({ message: 'Subscription variant created', type: 'success' });
    } catch (err) {
      console.error('Failed to create subscription variant:', err);
      setToast({ message: 'Failed to create variant: ' + err.message, type: 'error' });
    } finally {
      setSavingSubscription(false);
    }
  };

  const toggleReportSubscription = async (reportId, frequency, customScheduleData = null) => {
    if (!currentUser?.uid || savingSubscription) return;
    
    setSavingSubscription(true);
    try {
      const existingSub = reportSubscriptions.find(s => s.reportId === reportId);
      
      if (existingSub) {
        if (frequency === 'none') {
          // Remove subscription and its history
          await deleteSubscription(existingSub.id);
        } else {
          // Update frequency
          const updateData = {
            frequency,
            updatedAt: serverTimestamp()
          };
          if (frequency === 'custom' && customScheduleData) {
            updateData.customSchedule = customScheduleData;
            if (customScheduleData.dateRange) {
              updateData.dateRange = customScheduleData.dateRange;
            }
          }
          updateData.timezone = userTimezone || 'America/New_York';
          await updateDoc(doc(db, collectionPath('reportSubscriptions'), existingSub.id), updateData);
          await logUserAction({
            action: 'Report Subscription Updated',
            details: `Updated report subscription "${reportId}" (frequency: ${frequency})`,
            entityType: 'ReportSubscription',
            entityId: existingSub.id
          });
          setReportSubscriptions(prev => prev.map(s => 
            s.id === existingSub.id ? { ...s, frequency, customSchedule: customScheduleData } : s
          ));
        }
      } else if (frequency !== 'none') {
        // Create new subscription
        const newSub = {
          userId: currentUser.uid,
          userEmail: currentUser.email,
          reportId,
          frequency,
          timezone: userTimezone || 'America/New_York',
          dateRange: customScheduleData?.dateRange || null, // Store date range if provided
          createdAt: serverTimestamp(),
          isActive: true
        };
        if (frequency === 'custom' && customScheduleData) {
          newSub.customSchedule = customScheduleData;
        }
        // Generate unique document ID including dateRange for multiple subscriptions
        const dateRangeSuffix = customScheduleData?.dateRange ? `_${customScheduleData.dateRange}d` : '';
        const docId = generateTimestampId(`reportsub_${currentUser.uid}_${reportId}${dateRangeSuffix}`);
        await setDoc(doc(db, collectionPath('reportSubscriptions'), docId), newSub);
        setReportSubscriptions(prev => [...prev, { id: docId, ...newSub }]);
        await logUserAction({
          action: 'Report Subscription Created',
          details: `Subscribed to report "${reportId}" (frequency: ${frequency})`,
          entityType: 'ReportSubscription',
          entityId: docId
        });
      }
      setToast({ message: 'Subscription updated', type: 'success' });
    } catch (err) {
      console.error('Failed to update subscription:', err);
      setToast({ message: 'Failed to update subscription: ' + err.message, type: 'error' });
    } finally {
      setSavingSubscription(false);
    }
  };

  // Delete subscription and its history
  const deleteSubscription = async (subscriptionId) => {
    if (!subscriptionId || !currentUser?.uid) return;
    
    try {
      // Delete the subscription document
      const subDoc = reportSubscriptions.find(s => s.id === subscriptionId);
      await deleteDoc(doc(db, collectionPath('reportSubscriptions'), subscriptionId));
      
      // Delete associated send history
      const historyQuery = query(
        collection(db, collectionPath('reportSendHistory')),
        where('subscriptionId', '==', subscriptionId)
      );
      const historySnap = await getDocs(historyQuery);
      
      if (!historySnap.empty) {
        const batch = writeBatch(db);
        historySnap.docs.forEach(histDoc => {
          batch.delete(histDoc.ref);
        });
        await batch.commit();
        console.log(`Deleted ${historySnap.size} history records for subscription ${subscriptionId}`);
      }

      await logEntityDeletion({
        user: currentUser,
        entityType: 'ReportSubscription',
        entityId: subscriptionId,
        entityName: subDoc?.reportId || subscriptionId
      });
      
      setReportSubscriptions(prev => prev.filter(s => s.id !== subscriptionId));
      setToast({ message: 'Subscription and history deleted', type: 'success' });
    } catch (err) {
      console.error('Failed to delete subscription:', err);
      setToast({ message: 'Failed to delete subscription: ' + err.message, type: 'error' });
    }
  };

  // Pause/Resume subscription
  const togglePauseSubscription = async (reportId, isPaused, subscriptionId = null) => {
    // If subscriptionId provided, use it directly; otherwise find first matching reportId
    const existingSub = subscriptionId 
      ? reportSubscriptions.find(s => s.id === subscriptionId)
      : reportSubscriptions.find(s => s.reportId === reportId);
    if (!existingSub) return;
    
    try {
      await updateDoc(doc(db, collectionPath('reportSubscriptions'), existingSub.id), {
        isPaused,
        pauseReason: isPaused ? 'Manually paused by user' : null,
        updatedAt: serverTimestamp()
      });
      await logUserAction({
        action: isPaused ? 'Report Subscription Paused' : 'Report Subscription Resumed',
        details: `Subscription for report "${existingSub.reportId}" ${isPaused ? 'paused' : 'resumed'}`,
        entityType: 'ReportSubscription',
        entityId: existingSub.id
      });
      setReportSubscriptions(prev => prev.map(s =>
        s.id === existingSub.id ? { ...s, isPaused, pauseReason: isPaused ? 'Manually paused by user' : null } : s
      ));
      setToast({ message: isPaused ? 'Subscription paused' : 'Subscription resumed', type: 'success' });
    } catch (err) {
      console.error('Failed to toggle pause:', err);
      setToast({ message: 'Failed to update subscription', type: 'error' });
    }
  };

  // Fetch send history
  const fetchSendHistory = async (reportId = null, subscriptionId = null) => {
    setLoadingHistory(true);
    try {
      let q = query(
        collection(db, collectionPath('reportSendHistory')),
        where('userId', '==', currentUser.uid),
        orderBy('sentAt', 'desc')
      );
      
      // If subscriptionId provided, filter by specific subscription (preferred)
      if (subscriptionId) {
        q = query(
          collection(db, collectionPath('reportSendHistory')),
          where('userId', '==', currentUser.uid),
          where('subscriptionId', '==', subscriptionId),
          orderBy('sentAt', 'desc')
        );
      } else if (reportId) {
        // Otherwise filter by reportId (shows all variants)
        q = query(
          collection(db, collectionPath('reportSendHistory')),
          where('userId', '==', currentUser.uid),
          where('reportId', '==', reportId),
          orderBy('sentAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      const history = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSendHistory(history);
      setShowHistory(true);
      setSelectedHistoryReport(reportId);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setToast({ message: 'Failed to load send history', type: 'error' });
    } finally {
      setLoadingHistory(false);
    }
  };

  // Send report immediately (calls test function with sendImmediate flag)
  const sendReportNow = async (reportId, dateRange = null) => {
    // Rate limiting checks (per-report)
    const now = Date.now();
    const tenMinutesAgo = now - 10 * 60 * 1000;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    // Get timestamps for this specific report
    const reportTimestamps = sendTimestamps[reportId] || [];
    const recentSends = reportTimestamps.filter(ts => ts > tenMinutesAgo);
    const todaysSends = reportTimestamps.filter(ts => ts > todayStart.getTime());
    
    // Check 10-minute cooldown for this report
    if (recentSends.length > 0) {
      const lastSend = Math.max(...recentSends);
      const minutesSinceLast = Math.round((now - lastSend) / 60000);
      const minutesRemaining = 10 - minutesSinceLast;
      setToast({ 
        message: `Please wait ${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''} before sending this report again`, 
        type: 'warning' 
      });
      return;
    }
    
    // Check daily limit (4 per report per day)
    if (todaysSends.length >= 4) {
      setToast({ 
        message: 'Daily limit reached for this report (4 per day). Try again tomorrow.', 
        type: 'warning' 
      });
      return;
    }
    
    setPreviewingReport(reportId);
    setPreviewing(true);
    
    // Show processing message
    setToast({ message: '📊 Generating report... This may take a few seconds.', type: 'info' });
    
    try {
      const testFunction = httpsCallable(functions, 'testReportSubscriptions');
      const params = { 
        email: currentUser.email,
        reportId,
        sendImmediate: true // Flag to indicate this is a real send, not preview
      };
      if (dateRange) {
        params.dateRange = dateRange;
      }
      await testFunction(params);
      
      // Track this send for rate limiting (per-report)
      setSendTimestamps(prev => ({
        ...prev,
        [reportId]: [...(prev[reportId] || []), now].slice(-10) // Keep last 10 timestamps per report
      }));
      
      setToast({ message: `✅ Report sent to ${currentUser.email}!`, type: 'success' });
    } catch (err) {
      console.error('Send failed:', err);
      setToast({ message: 'Failed to send report: ' + err.message, type: 'error' });
    } finally {
      setPreviewingReport(null);
      setPreviewing(false);
    }
  };
  
  // Calculate remaining sends today and cooldown time (per-report)
  const getRateLimitInfo = (reportId) => {
    const now = Date.now();
    const tenMinutesAgo = now - 10 * 60 * 1000;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    // Get timestamps for this specific report
    const reportTimestamps = sendTimestamps[reportId] || [];
    const recentSends = reportTimestamps.filter(ts => ts > tenMinutesAgo);
    const todaysSends = reportTimestamps.filter(ts => ts > todayStart.getTime());
    
    const onCooldown = recentSends.length > 0;
    const minutesRemaining = onCooldown ? Math.ceil((10 * 60 * 1000 - (now - Math.max(...recentSends))) / 60000) : 0;
    const sendsRemaining = 4 - todaysSends.length;
    
    return { onCooldown, minutesRemaining, sendsRemaining };
  };

  // Notification management functions
  const markAsRead = async (notification) => {
    try {
      await updateDoc(doc(db, collectionPath('notifications'), notification.id), { 
        readBy: arrayUnion(currentUser.uid) 
      });
    } catch (e) {
      console.warn('markAsRead failed', e);
      setToast({ message: 'Failed to mark as read', type: 'error' });
    }
  };

  const markAsUnread = async (notification) => {
    try {
      await updateDoc(doc(db, collectionPath('notifications'), notification.id), { 
        readBy: arrayRemove(currentUser.uid) 
      });
    } catch (e) {
      console.warn('markAsUnread failed', e);
      setToast({ message: 'Failed to mark as unread', type: 'error' });
    }
  };

  const dismissNotification = async (notification) => {
    try {
      await updateDoc(doc(db, collectionPath('notifications'), notification.id), { 
        dismissedBy: arrayUnion(currentUser.uid) 
      });
      setToast({ message: 'Notification dismissed', type: 'success' });
    } catch (e) {
      console.warn('dismissNotification failed', e);
      setToast({ message: 'Failed to dismiss notification', type: 'error' });
    }
  };

  const bulkMarkRead = async () => {
    const batch = writeBatch(db);
    const unreadNotifications = filteredNotifications.filter(n => 
      !Array.isArray(n.readBy) || !n.readBy.includes(currentUser.uid)
    );
    
    if (unreadNotifications.length === 0) {
      setToast({ message: 'No unread notifications', type: 'info' });
      return;
    }

    unreadNotifications.forEach(n => {
      batch.update(doc(db, collectionPath('notifications'), n.id), { 
        readBy: arrayUnion(currentUser.uid) 
      });
    });

    try {
      await batch.commit();
      setToast({ message: `${unreadNotifications.length} notifications marked as read`, type: 'success' });
    } catch (e) {
      console.warn('bulkMarkRead failed', e);
      setToast({ message: 'Failed to mark all as read', type: 'error' });
    }
  };

  const bulkMarkUnread = async () => {
    const batch = writeBatch(db);
    const readNotifications = filteredNotifications.filter(n => 
      Array.isArray(n.readBy) && n.readBy.includes(currentUser.uid)
    );
    
    if (readNotifications.length === 0) {
      setToast({ message: 'No read notifications', type: 'info' });
      return;
    }

    readNotifications.forEach(n => {
      batch.update(doc(db, collectionPath('notifications'), n.id), { 
        readBy: arrayRemove(currentUser.uid) 
      });
    });

    try {
      await batch.commit();
      setToast({ message: `${readNotifications.length} notifications marked as unread`, type: 'success' });
    } catch (e) {
      console.warn('bulkMarkUnread failed', e);
      setToast({ message: 'Failed to mark all as unread', type: 'error' });
    }
  };

  const bulkDismiss = async () => {
    const batch = writeBatch(db);
    const undismissedNotifications = filteredNotifications.filter(n => 
      !Array.isArray(n.dismissedBy) || !n.dismissedBy.includes(currentUser.uid)
    );
    
    if (undismissedNotifications.length === 0) {
      setToast({ message: 'No notifications to dismiss', type: 'info' });
      return;
    }

    undismissedNotifications.forEach(n => {
      batch.update(doc(db, collectionPath('notifications'), n.id), { 
        dismissedBy: arrayUnion(currentUser.uid) 
      });
    });

    try {
      await batch.commit();
      setToast({ message: `${undismissedNotifications.length} notifications dismissed`, type: 'success' });
    } catch (e) {
      console.warn('bulkDismiss failed', e);
      setToast({ message: 'Failed to dismiss all', type: 'error' });
    }
  };

  const handleViewNotification = (notification) => {
    if (notification.related && notification.related.type) {
      markAsRead(notification);
      const entityType = notification.related.type;
      const entityId = notification.related.id;
      
      // Navigate to specific entity page if ID is available, otherwise to list page
      let route;
      if (entityType === 'Opportunity') {
        route = entityId ? `/opportunities/${entityId}` : '/opportunities';
      } else if (entityType === 'Task') {
        route = entityId ? `/tasks/${entityId}` : '/tasks';
      } else if (entityType === 'customer' || entityType === 'Customer') {
        route = entityId ? `/customers/${entityId}` : '/customers';
      } else if (entityType === 'Order') {
        route = entityId ? `/orders/${entityId}` : '/orders';
      } else {
        route = '/dashboard';
      }
      
      navigate(route);
    }
  };

  const cleanupOldNotifications = async () => {
    if (!window.confirm(
      'This will permanently delete all notifications older than 30 days. This action cannot be undone.\n\nContinue?'
    )) {
      return;
    }

    setCleaningUp(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const q = query(
        collection(db, collectionPath('notifications')),
        where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo))
      );
      
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      let count = 0;

      snapshot.docs.forEach((docSnap) => {
        batch.delete(docSnap.ref);
        count++;
      });

      if (count > 0) {
        await batch.commit();
        setToast({ 
          message: `Successfully deleted ${count} old notification${count > 1 ? 's' : ''}`, 
          type: 'success' 
        });
      } else {
        setToast({ message: 'No old notifications to delete', type: 'info' });
      }
    } catch (error) {
      console.error('Error cleaning up notifications:', error);
      setToast({ message: `Cleanup failed: ${error.message}`, type: 'error' });
    } finally {
      setCleaningUp(false);
    }
  };

  // Filter and paginate notifications
  const filteredNotifications = useMemo(() => {
    let filtered = notifications.filter(n => 
      !Array.isArray(n.dismissedBy) || !n.dismissedBy.includes(currentUser?.uid)
    );

    // Filter by tab
    if (activeTab === 'recent') {
      filtered = filtered.filter(n => n.level === 'info' || n.level === 'success');
    } else if (activeTab === 'important') {
      filtered = filtered.filter(n => n.level === 'warning' || n.level === 'urgent');
    }

    // Filter by search
    if (fb.applied.searchTerm) {
      const search = fb.applied.searchTerm.toLowerCase();
      filtered = filtered.filter(n => 
        n.title?.toLowerCase().includes(search) || 
        n.body?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [notifications, activeTab, fb.applied.searchTerm, currentUser]);

  const paginatedNotifications = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredNotifications.slice(start, start + PAGE_SIZE);
  }, [filteredNotifications, currentPage]);

  const totalPages = Math.ceil(filteredNotifications.length / PAGE_SIZE);
  const unreadCount = filteredNotifications.filter(n => 
    !Array.isArray(n.readBy) || !n.readBy.includes(currentUser?.uid)
  ).length;

  if (loading) {
    return (
      <main className="flex-grow p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-2 text-gray-600">Loading notifications...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl font-bold mb-2">Notifications Center</h2>
        <p className="text-gray-600 mb-6">
          Manage all your in-app notifications and email subscriptions in one place.
        </p>

        {/* Notifications Management Section */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6 border-b">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold">In-App Notifications</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''} 
                  {unreadCount > 0 && ` (${unreadCount} unread)`}
                </p>
              </div>
              
              {/* Search */}
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={fb.draft.searchTerm}
                  onChange={(e) => fb.setDraft('searchTerm', e.target.value)}
                  onKeyDown={fb.applyOnEnter}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                    fb.draft.searchTerm ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                  }`}
                />
              </div>
              <FilterActions onApply={() => { fb.apply(); setCurrentPage(1); }} onClear={() => { fb.clear(); setCurrentPage(1); }} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
              
              {/* Cleanup Button (Admin only) */}
              {hasPermission(currentUser, FEATURE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE) && (
                <button
                  onClick={cleanupOldNotifications}
                  disabled={cleaningUp}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                >
                  {cleaningUp ? 'Cleaning...' : '🗑️ Cleanup Old (30d+)'}
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex border-b mt-4 -mb-6">
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'all' 
                    ? 'border-indigo-600 text-indigo-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => { setActiveTab('all'); setCurrentPage(1); }}
              >
                All ({notifications.filter(n => !Array.isArray(n.dismissedBy) || !n.dismissedBy.includes(currentUser?.uid)).length})
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'recent' 
                    ? 'border-green-600 text-green-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => { setActiveTab('recent'); setCurrentPage(1); }}
              >
                Recent ({notifications.filter(n => 
                  (!Array.isArray(n.dismissedBy) || !n.dismissedBy.includes(currentUser?.uid)) &&
                  (n.level === 'info' || n.level === 'success')
                ).length})
              </button>
              <button 
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'important' 
                    ? 'border-red-600 text-red-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => { setActiveTab('important'); setCurrentPage(1); }}
              >
                Important ({notifications.filter(n => 
                  (!Array.isArray(n.dismissedBy) || !n.dismissedBy.includes(currentUser?.uid)) &&
                  (n.level === 'warning' || n.level === 'urgent')
                ).length})
              </button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {filteredNotifications.length > 0 && (
            <div className="px-6 py-3 bg-gray-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Bulk Actions:</span>
                {unreadCount > 0 ? (
                  <button
                    onClick={bulkMarkRead}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Mark All Read ({unreadCount})
                  </button>
                ) : (
                  <button
                    onClick={bulkMarkUnread}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium"
                  >
                    Mark All Unread
                  </button>
                )}
                <span className="text-gray-300">|</span>
                <button
                  onClick={bulkDismiss}
                  className="text-sm text-red-600 hover:text-red-800 font-medium"
                >
                  Dismiss All ({filteredNotifications.length})
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages || 1}
              </div>
            </div>
          )}

          {/* Notifications List */}
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {paginatedNotifications.length === 0 ? (
              <div className="p-12 text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p className="mt-4 text-gray-600">No notifications to display</p>
                <p className="mt-2 text-sm text-gray-500">
                  {fb.applied.searchTerm 
                    ? 'Try a different search term' 
                    : 'You\'re all caught up!'}
                </p>
              </div>
            ) : (
              paginatedNotifications.map(n => {
                const createdAt = n.createdAt?.seconds 
                  ? new Date(n.createdAt.seconds * 1000) 
                  : (n.createdAt ? new Date(n.createdAt) : null);
                const isUnread = !Array.isArray(n.readBy) || !n.readBy.includes(currentUser?.uid);
                
                // Level colors
                const levelColors = {
                  info: 'bg-blue-50 border-blue-200',
                  success: 'bg-green-50 border-green-200',
                  warning: 'bg-yellow-50 border-yellow-200',
                  urgent: 'bg-red-50 border-red-200'
                };
                
                const levelIcons = {
                  info: '💡',
                  success: '✅',
                  warning: '⚠️',
                  urgent: '🔴'
                };

                return (
                  <div 
                    key={n.id} 
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      isUnread ? 'bg-yellow-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Level Indicator */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full ${levelColors[n.level] || 'bg-gray-100 border-gray-200'} border flex items-center justify-center text-lg`}>
                        {levelIcons[n.level] || '📢'}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`text-sm font-medium ${isUnread ? 'font-bold' : ''}`}>
                            {n.title}
                          </h4>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {createdAt ? createdAt.toLocaleString() : ''}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{n.body}</p>
                        
                        {/* Actions */}
                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                          {isUnread ? (
                            <button 
                              onClick={() => markAsRead(n)}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              Mark Read
                            </button>
                          ) : (
                            <button 
                              onClick={() => markAsUnread(n)}
                              className="text-xs text-gray-600 hover:text-gray-800"
                            >
                              Mark Unread
                            </button>
                          )}
                          <button 
                            onClick={() => dismissNotification(n)}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Dismiss
                          </button>
                          {n.related && n.related.type && (
                            <button 
                              onClick={() => handleViewNotification(n)}
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                              View →
                            </button>
                          )}
                          <span className="text-xs text-gray-400">
                            {n.level === 'urgent' ? '🔴 Urgent' 
                              : n.level === 'warning' ? '⚠️ Warning'
                              : n.level === 'success' ? '✅ Success'
                              : '💡 Info'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredNotifications.length} pageSize={PAGE_SIZE} itemLabel="notifications" />
        </div>

        {/* Email Subscriptions Section */}
        <div className="bg-white rounded-lg shadow-lg mt-6">
          <div className="p-6 border-b">
            <h3 className="text-xl font-semibold">📧 Email Subscriptions</h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure email notifications and report subscriptions. Emails are sent to your account email.
            </p>
            {userTimezone && (
              <p className="text-xs text-indigo-600 mt-2">
                🌍 Your timezone is: <span className="font-mono font-semibold">{userTimezone}</span> - All scheduled times are in your local timezone
              </p>
            )}
          </div>

          <div className="p-6">
            {/* Report Subscriptions Section */}
            <div>
              <div className="mb-3">
                <h4 className="font-medium text-gray-900">Report Subscriptions</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Subscribe to receive reports via email on a schedule. Includes Task Summary for your pending/overdue tasks.
                </p>                
              </div>
              
              {loadingSubscriptions ? (
                <div className="text-gray-500 text-center py-4">Loading subscriptions...</div>
              ) : (
                <div className="space-y-3">
                  {/* Global Custom Schedule Panel */}
                  {showGlobalCustomSchedule && (
                    <div className="p-4 bg-indigo-50 border-2 border-indigo-300 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium text-indigo-900">Set Custom Schedule for All Reports</h5>
                        <button
                          onClick={() => setShowGlobalCustomSchedule(false)}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Cadence</label>
                          <select
                            value={globalCustomSchedule.cadence}
                            onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, cadence: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        {globalCustomSchedule.cadence === 'weekly' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Day of Week</label>
                            <select
                              value={globalCustomSchedule.day}
                              onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, day: e.target.value }))}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              <option value="monday">Monday</option>
                              <option value="tuesday">Tuesday</option>
                              <option value="wednesday">Wednesday</option>
                              <option value="thursday">Thursday</option>
                              <option value="friday">Friday</option>
                              <option value="saturday">Saturday</option>
                              <option value="sunday">Sunday</option>
                            </select>
                          </div>
                        )}
                        {globalCustomSchedule.cadence === 'monthly' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Day of Month</label>
                            <select
                              value={globalCustomSchedule.day || '1'}
                              onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, day: e.target.value }))}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                <option key={day} value={day}>{day}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Time (15-min intervals)</label>
                          <input
                            type="time"
                            step="900"
                            value={globalCustomSchedule.time}
                            onChange={(e) => setGlobalCustomSchedule(prev => ({ ...prev, time: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            // Warn user this will change ALL subscription times
                            const confirmed = window.confirm(
                              '⚠️ Warning: This will update ALL existing report subscriptions to use the same schedule time.\n\n' +
                              'Any date range variants you have created will also be updated to this schedule.\n\n' +
                              'Are you sure you want to continue?'
                            );
                            if (!confirmed) return;
                            
                            setSavingSubscription(true);
                            try {
                              // Apply to all reports - updates existing subscriptions
                              for (const report of AVAILABLE_REPORTS) {
                                await toggleReportSubscription(report.id, 'custom', globalCustomSchedule);
                              }
                              setToast({ message: 'Custom schedule applied to all reports', type: 'success' });
                              setShowGlobalCustomSchedule(false);
                            } catch (err) {
                              setToast({ message: 'Failed to apply schedule: ' + err.message, type: 'error' });
                            } finally {
                              setSavingSubscription(false);
                            }
                          }}
                          disabled={savingSubscription}
                          className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {savingSubscription ? 'Applying...' : 'Apply to All Reports'}
                        </button>
                        <button
                          onClick={() => setShowGlobalCustomSchedule(false)}
                          className="px-3 py-1 bg-gray-300 rounded text-sm hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Button to show global custom schedule */}
                  {!showGlobalCustomSchedule && (
                    <div className="flex justify-end mb-2">
                      <button
                        onClick={() => setShowGlobalCustomSchedule(true)}
                        className="px-3 py-1 text-sm bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 border border-indigo-300"
                      >
                        📅 Set Custom Schedule for All Reports
                      </button>
                    </div>
                  )}
                  {AVAILABLE_REPORTS.map(report => {
                    // Get ALL subscriptions for this report (to support multiple variants)
                    const subscriptions = reportSubscriptions.filter(s => s.reportId === report.id);
                    const hasSubscriptions = subscriptions.length > 0;
                    const showReportCustom = reportCustomStates[report.id]?.show || false;
                    // Default schedule for new variants
                    const defaultSchedule = { cadence: 'daily', day: 'monday', time: '07:00', dateRange: report.supportsDateRange ? 7 : null };
                    const reportCustomSchedule = reportCustomStates[report.id]?.schedule || defaultSchedule;
                    
                    return (
                      <div key={report.id} className="p-4 bg-gray-50 rounded-lg border hover:border-indigo-300 transition-colors space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-gray-900">{report.name}</div>
                              {report.supportsDateRange && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded" title="This report supports custom date ranges">
                                  📅 Date Range
                                </span>
                              )}
                              {hasSubscriptions && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                  {subscriptions.length} active
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{report.description}</div>
                            {/* Show all subscription variants */}
                            {subscriptions.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {subscriptions.map((sub, idx) => (
                                  <div key={sub.id} className="bg-white px-3 py-2 rounded border border-gray-200">
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                                        {sub.dateRange && (
                                          <span className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                            {sub.dateRange}d
                                          </span>
                                        )}
                                        {sub.customSchedule && (
                                          <span className="text-xs text-gray-600">
                                            📅 {sub.customSchedule.cadence === 'daily' ? 'Every day' : sub.customSchedule.cadence === 'weekly' ? `Weekly on ${sub.customSchedule.day.charAt(0).toUpperCase() + sub.customSchedule.day.slice(1)}` : `Monthly on day ${sub.customSchedule.day || 1}`} at {formatTime12Hour(sub.customSchedule.time)}
                                          </span>
                                        )}
                                        {sub.isPaused && (
                                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Paused</span>
                                        )}
                                        {sub.lastSendStatus === 'success' && (
                                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">✓ Sent</span>
                                        )}
                                        {sub.lastSendStatus === 'failed' && (
                                          <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded" title={sub.lastSendError}>
                                            ⚠️ Failed
                                          </span>
                                        )}
                                        {sub.consecutiveFailures > 0 && (
                                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded" title={`${sub.consecutiveFailures} consecutive failures`}>
                                            {sub.consecutiveFailures} failures
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => {
                                            // Populate edit form with current subscription values
                                            setEditingSubscription(sub);
                                            setReportCustomStates(prev => ({
                                              ...prev,
                                              [report.id]: {
                                                show: true,
                                                isNewVariant: false,
                                                schedule: {
                                                  cadence: sub.customSchedule?.cadence || 'daily',
                                                  day: sub.customSchedule?.day || 'monday',
                                                  time: sub.customSchedule?.time || '07:00',
                                                  dateRange: sub.dateRange || (report.supportsDateRange ? 7 : null)
                                                }
                                              }
                                            }));
                                          }}
                                          className="text-s text-blue-600 hover:text-blue-800"
                                          title="Edit subscription schedule"
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          onClick={() => togglePauseSubscription(report.id, !sub.isPaused, sub.id)}
                                          className="text-s text-gray-600 hover:text-gray-800"
                                          title={sub.isPaused ? 'Resume' : 'Pause'}
                                        >
                                          {sub.isPaused ? '▶️' : '⏸'}
                                        </button>
                                        <button
                                          onClick={() => sendReportNow(report.id, sub.dateRange)}
                                          disabled={previewing || getRateLimitInfo(report.id).sendsRemaining <= 0}
                                          className={`text-s ${previewing && previewingReport === report.id ? 'text-gray-400 cursor-wait' : getRateLimitInfo(report.id).sendsRemaining <= 0 ? 'text-gray-300 cursor-not-allowed' : 'text-green-600 hover:text-green-800'} disabled:opacity-50`}
                                          title={previewing && previewingReport === report.id ? 'Sending...' : getRateLimitInfo(report.id).onCooldown ? `Cooldown: ${getRateLimitInfo(report.id).minutesRemaining} min remaining` : getRateLimitInfo(report.id).sendsRemaining <= 0 ? 'Daily limit reached (4/day)' : `Send immediately (${getRateLimitInfo(report.id).sendsRemaining} remaining today)`}
                                        >
                                          {previewing && previewingReport === report.id ? '⏳' : '📤'}
                                        </button>
                                        <button
                                          onClick={() => {
                                            fetchSendHistory(report.id, sub.id);
                                            setSelectedHistoryReport(report.id);
                                          }}
                                          className="text-s text-blue-600 hover:text-blue-800"
                                          title="View send history for this subscription"
                                        >
                                          📜
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (window.confirm('Delete this subscription and its send history?')) {
                                              await deleteSubscription(sub.id);
                                            }
                                          }}
                                          className="text-xs text-red-600 hover:text-red-800"
                                          title="Delete subscription and history"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </div>
                                    {/* Last Sent Time */}
                                    {sub.lastSentAt && (
                                      <div className="text-xs text-gray-400 mt-1">
                                        Last sent: {new Date(sub.lastSentAt.toDate ? sub.lastSentAt.toDate() : sub.lastSentAt).toLocaleString(undefined, { 
                                          timeZone: userTimezone || 'America/Phoenix',
                                          dateStyle: 'short',
                                          timeStyle: 'short'
                                        })} {userTimezone && `(${userTimezone.split('/')[1]})`}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Add Variant button for reports that support date ranges */}
                            {report.supportsDateRange && hasSubscriptions && (
                              <button
                                onClick={() => {
                                  setReportCustomStates(prev => ({
                                    ...prev,
                                    [report.id]: { 
                                      show: true, 
                                      schedule: defaultSchedule, 
                                      isNewVariant: true 
                                    }
                                  }));
                                }}
                                disabled={savingSubscription}
                                className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 font-medium"
                                title="Add another subscription with different date range"
                              >
                                ➕ Add Variant
                              </button>
                            )}
                            {/* Add first subscription button */}
                            {!hasSubscriptions && (
                              <button
                                onClick={() => {
                                  setReportCustomStates(prev => ({
                                    ...prev,
                                    [report.id]: { 
                                      show: true, 
                                      schedule: defaultSchedule, 
                                      isNewVariant: true 
                                    }
                                  }));
                                }}
                                disabled={savingSubscription}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-medium"
                                title="Subscribe to this report"
                              >
                                ➕ Subscribe
                              </button>
                            )}
                          </div>
                        </div>
                        {showReportCustom && (
                          <div className={`ml-4 p-3 border rounded space-y-2 ${editingSubscription ? 'bg-blue-50 border-blue-300' : 'bg-white'}`}>
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              {editingSubscription ? '✏️ Edit Subscription' : reportCustomStates[report.id]?.isNewVariant ? '➕ Add New Variant' : '📅 Set Custom Schedule'}
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Cadence</label>
                                <select
                                  value={reportCustomSchedule.cadence}
                                  onChange={(e) => {
                                    const cadence = e.target.value;
                                    setReportCustomStates(prev => ({
                                      ...prev,
                                      [report.id]: {
                                        ...prev[report.id],
                                        schedule: { ...reportCustomSchedule, cadence }
                                      }
                                    }));
                                  }}
                                  className="border rounded px-2 py-1 text-sm"
                                >
                                  <option value="daily">Daily</option>
                                  <option value="weekly">Weekly</option>
                                  <option value="monthly">Monthly</option>
                                </select>
                              </div>
                              {reportCustomSchedule.cadence === 'weekly' && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Day of Week</label>
                                  <select
                                    value={reportCustomSchedule.day}
                                    onChange={(e) => setReportCustomStates(prev => ({
                                      ...prev,
                                      [report.id]: {
                                        ...prev[report.id],
                                        schedule: { ...reportCustomSchedule, day: e.target.value }
                                      }
                                    }))}
                                    className="border rounded px-2 py-1 text-sm"
                                  >
                                    <option value="monday">Monday</option>
                                    <option value="tuesday">Tuesday</option>
                                    <option value="wednesday">Wednesday</option>
                                    <option value="thursday">Thursday</option>
                                    <option value="friday">Friday</option>
                                    <option value="saturday">Saturday</option>
                                    <option value="sunday">Sunday</option>
                                  </select>
                                </div>
                              )}
                              {reportCustomSchedule.cadence === 'monthly' && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Day of Month</label>
                                  <select
                                    value={reportCustomSchedule.day || '1'}
                                    onChange={(e) => setReportCustomStates(prev => ({
                                      ...prev,
                                      [report.id]: {
                                        ...prev[report.id],
                                        schedule: { ...reportCustomSchedule, day: e.target.value }
                                      }
                                    }))}
                                    className="border rounded px-2 py-1 text-sm"
                                  >
                                    {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                                      <option key={day} value={day}>{day}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Time (15-min intervals)</label>
                                <input
                                  type="time"
                                  step="900"
                                  value={reportCustomSchedule.time}
                                  onChange={(e) => setReportCustomStates(prev => ({
                                    ...prev,
                                    [report.id]: {
                                      ...prev[report.id],
                                      schedule: { ...reportCustomSchedule, time: e.target.value }
                                    }
                                  }))}
                                  className="border rounded px-2 py-1 text-sm"
                                />
                              </div>
                              {/* Date Range Selector - only for reports that support it */}
                              {report.supportsDateRange && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">📅 Date Range</label>
                                  <select
                                    value={reportCustomSchedule.dateRange || 7}
                                    onChange={(e) => setReportCustomStates(prev => ({
                                      ...prev,
                                      [report.id]: {
                                        ...prev[report.id],
                                        schedule: { ...reportCustomSchedule, dateRange: Number(e.target.value) }
                                      }
                                    }))}
                                    className="border rounded px-2 py-1 text-sm bg-indigo-50 border-indigo-300"
                                  >
                                    {DATE_RANGE_OPTIONS.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  if (editingSubscription) {
                                    // Update existing subscription
                                    try {
                                      const subRef = doc(db, collectionPath('reportSubscriptions'), editingSubscription.id);
                                      await updateDoc(subRef, {
                                        customSchedule: reportCustomSchedule,
                                        dateRange: reportCustomSchedule.dateRange || null,
                                        frequency: 'custom',
                                        timezone: userTimezone || 'America/New_York',
                                        updatedAt: serverTimestamp()
                                      });
                                      setToast({ type: 'success', message: `✅ Subscription updated successfully!` });
                                      setEditingSubscription(null);
                                      setReportCustomStates(prev => ({
                                        ...prev,
                                        [report.id]: { ...prev[report.id], show: false }
                                      }));
                                    } catch (err) {
                                      console.error('Error updating subscription:', err);
                                      setToast({ type: 'error', message: 'Failed to update subscription' });
                                    }
                                  } else {
                                    const isNewVariant = reportCustomStates[report.id]?.isNewVariant;
                                    if (isNewVariant) {
                                      // Create a new variant (don't update existing)
                                      createSubscriptionVariant(report.id, reportCustomSchedule);
                                    } else {
                                      // Update existing subscription
                                      toggleReportSubscription(report.id, 'custom', reportCustomSchedule);
                                    }
                                    setReportCustomStates(prev => ({
                                      ...prev,
                                      [report.id]: { ...prev[report.id], show: false, isNewVariant: false }
                                    }));
                                  }
                                }}
                                disabled={savingSubscription}
                                className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50"
                              >
                                {editingSubscription ? '💾 Update' : reportCustomStates[report.id]?.isNewVariant ? 'Add Variant' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingSubscription(null);
                                  setReportCustomStates(prev => ({
                                    ...prev,
                                    [report.id]: { ...prev[report.id], show: false }
                                  }));
                                }}
                                className="px-2 py-1 bg-gray-300 rounded text-xs hover:bg-gray-400"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {reportSubscriptions.length > 0 && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm text-green-800">
                    ✅ You have {reportSubscriptions.length} active report subscription{reportSubscriptions.length !== 1 ? 's' : ''}.
                    Reports will be sent to <strong>{currentUser?.email}</strong>.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                📜 Report Send History
                {selectedHistoryReport && ` - ${AVAILABLE_REPORTS.find(r => r.id === selectedHistoryReport)?.name}`}
              </h3>
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedHistoryReport(null);
                  setSendHistory([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              {loadingHistory ? (
                <div className="text-center py-8 text-gray-500">Loading history...</div>
              ) : sendHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No send history found</div>
              ) : (
                <div className="space-y-2">
                  {sendHistory.map(entry => (
                    <div 
                      key={entry.id} 
                      className={`p-3 rounded-lg border ${
                        entry.status === 'success' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-lg ${entry.status === 'success' ? '' : '⚠️'}`}>
                              {entry.status === 'success' ? '✅' : '❌'}
                            </span>
                            <div>
                              <div className="font-medium text-gray-900">
                                {AVAILABLE_REPORTS.find(r => r.id === entry.reportId)?.name || entry.reportId}
                              </div>
                              <div className="text-sm text-gray-600">{entry.reportSubject}</div>
                            </div>
                          </div>
                          {entry.error && (
                            <div className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded">
                              <strong>Error:</strong> {entry.error}
                              {entry.metadata?.retries && (
                                <span className="ml-2">(Failed after {entry.metadata.retries} retries)</span>
                              )}
                            </div>
                          )}
                          {entry.metadata && (
                            <div className="mt-2 text-xs text-gray-500">
                              Schedule: {entry.metadata.frequency}
                              {entry.metadata.customSchedule && (
                                <span> - {entry.metadata.customSchedule.cadence} at {entry.metadata.customSchedule.time}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 text-right">
                          {new Date(entry.sentAt.toDate ? entry.sentAt.toDate() : entry.sentAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => fetchSendHistory(null)}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                {selectedHistoryReport ? '📊 Show All Reports' : 'Showing all reports'}
              </button>
              <button
                onClick={() => {
                  setShowHistory(false);
                  setSelectedHistoryReport(null);
                  setSendHistory([]);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default NotificationsPage;


